import { useEffect, useMemo, useRef, useState } from "react";
import { Select, MenuItem, FormControl, InputLabel, CircularProgress } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import Footer from "./components/Footer.js";
import AutoCompleteComponent from "./components/AutoCompleteComponent.js";
import isAuthenticated from "../../components/Utils/isAuthenticated.js";
import { courierDepts } from "./utils/common.js";
import MultipleChoiceExam from "./multipleChoice/MultipleChoiceExam.js";
import {
	getLatestRFSByCCID,
	getScannerRequestByReqnum,
	getClassesFromDepartmentsRFS,
} from "../../components/Api/Getters.js";
import Assign2 from "./Assign2/assign2.js";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import ArticleIcon from "@mui/icons-material/Article";
import CalculateIcon from "@mui/icons-material/Calculate";
import EditIcon from "@mui/icons-material/Edit";
import { getLDAPUserBySearchField } from "../../components/Api/Getters.js";
import OfficeDialog from "./components/OfficeDialog.js";
import { useAtom, useSetAtom } from "jotai";
import { scannerRequestStateAtom, advancedSettingsAtom } from "../../components/Atoms/atoms.js";
import { buildDoc } from "./utils/pdfReceipt.js";
import { serializeScannerStateForApi } from "./utils/common.js";
import { updateRFSData } from "../../components/Api/Updates.js";
import { RESET } from "jotai/utils";
import ErrorIcon from "@mui/icons-material/Error";
import EmailRecipientField, {
	buildFacultyRecipientOption,
	normalizeRecipientSelection,
} from "../../components/Utils/EmailRecipientField.js";
import { useFacultyRank } from "../../Hooks/useFacultyRankQuery";

//Save reorder sheet in O drive => NOT GOING TO BE EASY
//Create an admin panel where we can change status to "done", print out sheet, make requisitions for profs, etc.
//finish mrscor and advanced functions
//implement pickup or delivery

const inputProps = {
	variant: "outlined",
	minRows: 8,
	maxRows: 8,
	maxLength: 1000,
};

function compareClassTitles(leftTitle, rightTitle) {
	const parseTitle = (title) => {
		const normalizedTitle = String(title || "").trim();
		const parts = normalizedTitle.split(" ");

		if (parts.length === 5) {
			return [parts[0] + " " + parts[1], parts[2], parts[3], parts[4]];
		}

		return parts;
	};

	const leftParts = parseTitle(leftTitle);
	const rightParts = parseTitle(rightTitle);
	const maxLength = Math.max(leftParts.length, rightParts.length);

	for (let index = 0; index < maxLength; index += 1) {
		const leftPart = leftParts[index] ?? "";
		const rightPart = rightParts[index] ?? "";

		if (leftPart < rightPart) {
			return -1;
		}

		if (leftPart > rightPart) {
			return 1;
		}
	}

	return 0;
}

export default function RequestForService() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams(); // ⬅️ add this
	const reqnumFromQuery = searchParams.get("reqnum"); // ⬅️ add this
	const auth = isAuthenticated();
	const token = auth.token;
	const { data: facultyRank = [], isLoading: facultyRankLoading } = useFacultyRank(token);

	const access = auth.access;
	// const access = "OFFICE";
	const ccidFromAuth = auth.ccid;
	const firstNameFromAuth = auth.firstName;
	const lastNameFromAuth = auth.lastName;
	const userDeptFromAuth = auth.dept_ids.includes(",")
		? auth.dept_ids.split(",")[0]
		: auth.dept_ids;
	const setAdvancedSettings = useSetAtom(advancedSettingsAtom);

	const [ccid, setCCID] = useState(access.includes("OFFICE") ? "" : ccidFromAuth);
	const [state, setState] = useAtom(scannerRequestStateAtom);
	const [typeOfExam, setTypeOfExam] = useState("");
	const [gotDepartments, setGotDepartments] = useState(false);
	const [dataFilled, setDataFilled] = useState(false);
	const [classNumList, setClassNumList] = useState([{}]);
	const [departments, setDepartments] = useState([]);
	const [departmentsDict, setDepartmentsDict] = useState([{ key: "", value: "", label: "" }]);
	const examTypeRef = useRef(null);
	const [errorState, setErrorState] = useState({
		address: false,
		deptID: false,
		email1: false,
		instructorName: false,
		delivery: false,
	});
	const [instructorClasses, setInstructorClasses] = useState([]);
	const [searchedByCCID, setSearchedByCCID] = useState(false);
	const [openOfficeDialog, setOpenOfficeDialog] = useState(false);
	const [ccidString, setCCIDString] = useState("");
	const [confirmedOfficeInstructor, setConfirmedOfficeInstructor] = useState(false);
	const [searchState, setSearchState] = useState({
		ccid: "",
		firstName: "",
		lastName: "",
		instructorName: "",
		email: "",
		empid: "",
	});
	const [searchError, setSearchError] = useState(false);
	const [loadingSearchByCCID, setLoadingSearchByCCID] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [loadingEdit, setLoadingEdit] = useState(false);
	const [loadingProfilePrefill, setLoadingProfilePrefill] = useState(false);

	const [isFormFilled, setIsFormFilled] = useState(false);

	const facultyRecipientOptions = useMemo(
		() => facultyRank.map((entry) => buildFacultyRecipientOption(entry)).filter(Boolean),
		[facultyRank],
	);
	const facultyRecipientsByCcid = useMemo(() => {
		const next = new Map();
		facultyRecipientOptions.forEach((recipient) => {
			if (!next.has(recipient.ccid)) {
				next.set(recipient.ccid, recipient);
			}
		});
		return next;
	}, [facultyRecipientOptions]);
	const selectedPrimaryEmail = useMemo(
		() =>
			normalizeRecipientSelection(state.email1 ? [state.email1] : [], facultyRecipientsByCcid)[0] ||
			null,
		[facultyRecipientsByCcid, state.email1],
	);
	const selectedSecondaryEmail = useMemo(
		() =>
			normalizeRecipientSelection(state.email2 ? [state.email2] : [], facultyRecipientsByCcid)[0] ||
			null,
		[facultyRecipientsByCcid, state.email2],
	);

	const officeInstructorDefaults = useMemo(
		() => ({
			ccid: searchState.ccid?.trim() || "",
			instructorName: [searchState.firstName, searchState.lastName]
				.filter(Boolean)
				.join(" ")
				.trim(),
			email1: searchState.email?.trim() || "",
		}),
		[searchState.ccid, searchState.email, searchState.firstName, searchState.lastName],
	);

	const signedInUserDefaults = useMemo(
		() => ({
			ccid: ccidFromAuth,
			instructorName: [firstNameFromAuth, lastNameFromAuth].filter(Boolean).join(" ").trim(),
			email1: ccidFromAuth ? `${ccidFromAuth}@ualberta.ca` : "",
			deptID: userDeptFromAuth || "",
		}),
		[ccidFromAuth, firstNameFromAuth, lastNameFromAuth, userDeptFromAuth],
	);

	const missingRequiredFields = useMemo(() => {
		const missing = [];

		if (!state.instructorName) missing.push("Instructor Name");
		if (!state.deptID) missing.push("Department");
		if (!state.email1) missing.push("Primary Email");
		if (!state.delivery) missing.push("Sheet Return/Delivery");

		const deliveryNeedsAddress = state.delivery === "Campus Mail";
		const deliveryIsCourier = state.delivery === "Courier";

		if (deliveryNeedsAddress && !state.address) missing.push("Return Address");
		if (deliveryIsCourier && state.deptID && !courierDepts.includes(Number(state.deptID))) {
			missing.push("Valid Sheet Return Method");
		}

		return missing;
	}, [state.address, state.deptID, state.delivery, state.email1, state.instructorName]);

	const missingFieldsLabel = missingRequiredFields.join(", ");

	useEffect(() => {
		if (
			state.delivery === "Campus Mail" &&
			state.address.toLowerCase().replace(/\s/g, "") === "pickup"
		) {
			setErrorState((prev) => ({ ...prev, address: true }));
			setState((prev) => ({
				...prev,
				address: "",
			}));
		}
	}, [setState, state.delivery, state.address]);

	function buildKeysStateFromApi(apiKeysArray) {
		if (!apiKeysArray || !Array.isArray(apiKeysArray) || apiKeysArray.length === 0) {
			return {};
		}

		const out = {};

		apiKeysArray.forEach((keyObj) => {
			const version = keyObj.version ? String(keyObj.version).trim() : "1";

			// We'll build a per-question lookup for this version
			const itemsByQuestion = {};

			if (Array.isArray(keyObj.items)) {
				keyObj.items.forEach((item) => {
					const qNum = Number(item.item_no); // question number on the sheet
					if (isNaN(qNum)) return;

					itemsByQuestion[qNum] = {
						// omit is "Y" / "N" in the API; store boolean for convenience
						omit: item.omit === "Y",
						// weight is "1.000" string -> number 1
						weight: item.weight !== undefined && item.weight !== null ? Number(item.weight) : 1,
						// freeform notes per item
						notes: item.notes || "",
						mr: item.mr === "Y",
						// keep raw answers data in case modal needs to show it / eventually re-send it
						answers: item.answers || [],
						modified_answer: item.modified_answer || null,
						original_answer: item.original_answer || [],
					};
				});
			}

			out[version] = {
				version,
				exm_id: keyObj.exm_id,
				reqnum: keyObj.reqnum,
				process_date: keyObj.process_date,

				contiguous: keyObj.contiguous,
				q_ranges: keyObj.q_ranges,
				itemsByQuestion,
			};
		});

		return out;
	}

	function mapApiToScannerState(api, prevState) {
		// shape this to your actual payload field names from /scanner/request/<reqnum>/
		// (below is a safe/common example—adjust keys if your API differs)

		let classIdValues = [];
		let assign2StateValues = {};
		if (api.class_ids && api.class_ids.length > 0 && Number(api.program) !== 4) {
			classIdValues = api.class_ids.split(",").map((id) => Number(id));
		} else if (Number(api.program) === 4) {
			let examDateValue = new Date(api.received);
			let part1 = examDateValue.toISOString().split("T")[0];
			let part2 = examDateValue.toISOString().split("T")[1].split("-")[0];
			let part2Formatted = part2.substring(0, 5);
			examDateValue = part1 + "T" + part2Formatted;
			assign2StateValues = {
				booklets: api.booklets,
				sheets: api.sheets,
				sided: api.sided === "double" ? "Double sided" : "Single sided",
				examDate: examDateValue,
				exam_type: api.exam_type,
				tsqs_upload: api.tsqs_upload === 1 ? "upload" : "doNotUpload",
			};
		}
		//api.keys is now an array of examkeyitems objects, use to pre-fill advancedSettings

		const keysState = buildKeysStateFromApi(api.keys);

		const isWeightedExam = api.keys.some((key) =>
			key.items.some((item) => Number(item.weight) !== 1),
		);
		const isMRExam = api.keys.some((key) => key.items.some((item) => item.mr === "Y"));
		if (isWeightedExam) {
			setAdvancedSettings((prev) => ({
				...prev,
				compositeScoring: true,
			}));
		}
		if (isMRExam) {
			setAdvancedSettings((prev) => ({
				...prev,
				mrscor: true,
			}));
		}

		return {
			...prevState,
			reqnum: api.reqnum ?? "",
			instructorName: api.instructor_name ?? "",
			email1: api.email1 ?? "",
			email2: api.email2 ?? "",
			phone: api.phone ?? "",
			deptID: api.dept ? Number(api.dept) : "",
			courseTitle: api.coursetitle ?? "",
			department: api.department_label ?? api.caddr2 ?? "",
			delivery:
				api.delivery?.length > 0
					? api.delivery
					: courierDepts.includes(Number(api.dept))
						? "Courier"
						: "",
			address: api.caddr3 ?? "",
			program: String(api.program ?? "1"),
			// exam section
			lqNum: api.lqnum ? Number(api.lqnum) : 1,
			hqNum: api.hqnum ? Number(api.hqnum) : "",
			qLen: api.qlen ? Number(api.qlen).toString() : "5",
			nKeys: String(api.nkeys ?? "1"),
			a: api.a ?? "",
			b: api.b ?? "",
			c: api.c ?? "0",
			d: api.d ?? "",
			e: api.e ?? "0",
			f: api.f ?? "0",
			k: api.k ?? "",
			l: api.l ?? "",
			m: api.m ?? "",
			n: String(api.n ?? "1"),
			o: api.o ?? "0",
			p: api.p ?? "0",
			status: api.status ?? "",
			// MR settings (only if your API returns these)
			wholeExamMR: api.wholeExamMR ?? "",
			multipleResponseWeighted: api.multipleResponseWeighted ?? "",
			weightingMultiple: api.weightingMultiple ?? "",
			schema: api.schema ?? "",
			schemaNotes: api.schemaNotes ?? "",
			numMultipleResponse: api.numMultipleResponse ?? "",
			numSingleResponse: api.numSingleResponse ?? "",
			multipleResponseQuestions: api.multipleResponseQuestions ?? [],
			singleResponseQuestions: api.singleResponseQuestions ?? [],
			// classes
			class_ids: classIdValues,
			// notes
			notes: api.notes ?? "",
			// assign2 specific
			...assign2StateValues,
			keys: keysState,
		};
	}

	useEffect(() => {
		if (access === "OFFICE" && ccid === "") {
			setOpenOfficeDialog(true);
		} else {
			setOpenOfficeDialog(false);
		}
	}, [access, ccid]);

	useEffect(() => {
		let token = sessionStorage.getItem("token");
		if (!reqnumFromQuery) {
			setEditMode(false);
			setLoadingEdit(false);
			return;
		}

		setEditMode(true);
		setLoadingEdit(true);

		(async () => {
			try {
				const data = await getScannerRequestByReqnum(token, ccid, reqnumFromQuery);

				if (!data) throw new Error(`Failed to fetch request #${reqnumFromQuery}`);

				if (data && data.status && data.status !== "Received") {
					alert(
						`Request #${reqnumFromQuery} has already been scanned and uploaded. Modifications to the request are not permitted at this time.`,
					);
					return navigate("/view-scanning-requests");
				}
				setState((prev) => mapApiToScannerState(data, prev));

				const nextType = String(data.program) === "4" ? "assign2" : "multipleChoice";
				setTypeOfExam(nextType);
			} catch (e) {
				console.error(e);

				alert(
					"Invalid requisition number or you do not have access to edit this. If you think this is a mistake, please contact TSQS.",
				);
				navigate("/request-for-service");
				window.location.reload();
			} finally {
				setLoadingEdit(false);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reqnumFromQuery]);

	useEffect(() => {
		if (dataFilled === false && ccid !== "" && !reqnumFromQuery && !editMode) {
			setLoadingProfilePrefill(true);
			handleGetRFSListByCCID(
				ccid,
				access.includes("OFFICE") ? officeInstructorDefaults : signedInUserDefaults,
			);
		}
		// eslint-disable-next-line
	}, [dataFilled, ccid, access, reqnumFromQuery, editMode]);

	useEffect(() => {
		if (token !== "") {
			// getResponse(token);
			getDepartments(token);
		}

		async function getDepartments(token) {
			if (!gotDepartments) {
				let url = process.env.REACT_APP_BACKEND_HOST.concat("/scanner/departments/");
				const response = await fetch(url, {
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						Authorization: `Token ${token}`,
					},
				});

				let output = await response.json();
				if (output.length > 0) {
					Promise.all(output).then((result) => {
						result.forEach((department) => {
							department.DNAME = department.DNAME.replace("Department of", "");
						});
						//sort by dname
						result.sort((a, b) => (a.DNAME > b.DNAME ? 1 : -1));
						setDepartments(result);
					});
					setGotDepartments(true);
				}
			} else {
				let i = 1;
				let departmentsDict = [];

				departments.forEach((department) => {
					//remove "Department of" from the beginning of each dname

					departmentsDict.push({
						key: i,
						value: department.DEPT_ID,
						label: department.DNAME.trim() + " (" + department.DEPT_ID + ")",
					});
					i++;
				});
				setDepartmentsDict(departmentsDict);
			}
		}
	}, [token, departments, gotDepartments]);

	useEffect(() => {
		//when typeOfExam changes, jump to ref
		if (typeOfExam && !reqnumFromQuery) {
			examTypeRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [typeOfExam, reqnumFromQuery]);

	useEffect(() => {
		if (courierDepts.includes(state.deptID) && state.delivery.toLowerCase() !== "pick up") {
			setState({
				...state,
				delivery: "Courier",
			});
		}
		// eslint-disable-next-line
	}, [state.deptID]);

	useEffect(() => {
		if (classNumList && classNumList.length > 0) {
			let instructorClasses = classNumList.filter((classItem) => classItem.ccid === ccid);

			setInstructorClasses(instructorClasses);
		}
	}, [classNumList, ccid]);

	useEffect(() => {
		const filled = missingRequiredFields.length === 0;
		setIsFormFilled(filled);

		if (!filled && typeOfExam) {
			setTypeOfExam("");
		}
	}, [missingRequiredFields, typeOfExam]);

	// expose update/submit functions to child exam components
	async function updateRequestOnServer(reqnum, payload, token) {
		const response = await updateRFSData(token, reqnum, payload);

		if (response.status !== 201) {
			console.log("Failed to update request", response);
			alert("Failed to update request. Please try again.");
		}
		return response;
	}

	const handleEditSubmit = async () => {
		const token = sessionStorage.getItem("token");
		const payload = serializeScannerStateForApi(state);
		await updateRequestOnServer(reqnumFromQuery, payload, token);

		// Optional: regenerate the PDF with updated info
		await handlePrint(reqnumFromQuery);

		// Bounce them back to the list
		setState(RESET);
		navigate("/view-scanning-requests");
	};

	const handleSearchByCCID = async () => {
		setLoadingSearchByCCID(true);
		const apiData = await getLDAPUserBySearchField(token, "uid", ccidString);

		if (apiData && !apiData.error) {
			if (
				apiData.ccid.length > 0 &&
				apiData.email.length > 0 &&
				apiData.empid.length > 0 &&
				apiData.firstName.length > 0 &&
				apiData.lastName.length > 0
			) {
				setSearchState({
					ccid: apiData.ccid,
					firstName: apiData.firstName,
					lastName: apiData.lastName,
					instructorName: apiData.firstName + " " + apiData.lastName,
					email: apiData.email,
					empid: apiData.empid,
				});
				setSearchedByCCID(true);
				setSearchError(false);
				setLoadingSearchByCCID(false);
			}
		} else {
			setSearchedByCCID(false);
			setSearchError(true);
			setLoadingSearchByCCID(false);
		}
	};

	const handleConfirmOfficeInstructor = async () => {
		setConfirmedOfficeInstructor(true);

		if (searchState.ccid) {
			setLoadingProfilePrefill(true);
			setDataFilled(false);
			setState((prev) => ({
				...prev,
				instructorName: officeInstructorDefaults.instructorName,
				email1: officeInstructorDefaults.email1,
			}));
			setCCID(searchState.ccid);
		} else {
			alert("Error: No CCID found for the instructor.");
		}
	};

	const handleGetRFSListByCCID = async (ccid, fallbackProfile = {}) => {
		if (editMode || reqnumFromQuery) return;
		try {
			const response = await getLatestRFSByCCID(token, ccid);

			if (response) {
				setState((prev) => ({
					...prev,
					instructorName:
						response.instructor_name || fallbackProfile.instructorName || prev.instructorName,
					department: response?.caddr2?.trim?.() || prev.department,
					deptID: response.dept ? Number(response.dept) : prev.deptID,
					email1:
						response.email1 ||
						fallbackProfile.email1 ||
						(fallbackProfile.ccid ? `${fallbackProfile.ccid}@ualberta.ca` : prev.email1),
					phone: response.phone || prev.phone,
					address: response.caddr3 || prev.address,
					delivery: response.delivery || prev.delivery,
					pocketflag: "",
					numOpen: prev.numOpen || 1,
					docSize: prev.docSize || 1,
					singleSided: prev.singleSided || true,
				}));
			} else {
				setState((prev) => ({
					...prev,
					instructorName: fallbackProfile.instructorName || prev.instructorName,
					deptID: fallbackProfile.deptID || prev.deptID,
					email1:
						fallbackProfile.email1 ||
						(fallbackProfile.ccid ? `${fallbackProfile.ccid}@ualberta.ca` : prev.email1),
				}));
			}

			setDataFilled(true);
		} catch (error) {
			setDataFilled(true);
			console.error(error);
		} finally {
			setLoadingProfilePrefill(false);
		}
	};

	const handleChange = (event) => {
		const value = event.target.value;
		setState({
			...state,
			[event.target.name]: value,
		});
		if (event.target.name === "instructorName") {
			setErrorState((prev) => ({ ...prev, instructorName: false }));
		}
		if (event.target.name === "email1") {
			setErrorState((prev) => ({ ...prev, email1: false }));
		}

		if (event.target.name === "address") {
			setErrorState((prev) => ({ ...prev, address: false }));
		}
		if (event.target.name === "delivery") {
			setErrorState((prev) => ({ ...prev, delivery: false }));
		}
	};

	const handleChangeAutoComplete = (event, value) => {
		if (value) {
			setState({
				...state,
				department: value.label,
				deptID: value.value,
			});
			setErrorState((prev) => ({ ...prev, deptID: false }));
		}
	};

	const handleEmailRecipientChange = (fieldName, recipient) => {
		setState((prev) => ({
			...prev,
			[fieldName]: recipient?.email || "",
		}));

		if (fieldName === "email1") {
			setErrorState((prev) => ({ ...prev, email1: false }));
		}
	};

	useEffect(() => {
		if (state.deptID && state.deptID !== "") {
			(async () => {
				const response = await getClassesFromDepartmentsRFS(token, state.deptID);
				let listClassNum = [];
				if (response.length > 0) {
					response.forEach((classFromDept) => {
						const listClassObj = {
							key: classFromDept.CLASSID,
							value: classFromDept.CLASSID,
							label: classFromDept.CTITLE,
							name: classFromDept.NAME,
							term: classFromDept.TERM_NAME,
							term_value: classFromDept.TERM,
							term_start: classFromDept.TERM_START,
							term_end: classFromDept.TERM_END,
							ccid: classFromDept.CCID,
							combine: classFromDept.COMBINE,
						};
						listClassNum.push(listClassObj);
					});

					const sorted = [...listClassNum].sort((leftClass, rightClass) => {
						const leftTermValue = Number(leftClass.term_value) || 0;
						const rightTermValue = Number(rightClass.term_value) || 0;

						if (leftTermValue !== rightTermValue) {
							return leftTermValue - rightTermValue;
						}

						return compareClassTitles(leftClass.label, rightClass.label);
					});

					setClassNumList(sorted);
				}
			})();
		}
	}, [state.deptID, token]);

	// This function triggers the actual printing/saving.

	const handlePrint = async (pulledReqnum) => {
		// Build a new document with the latest state
		const doc = buildDoc(state, pulledReqnum);

		// Optionally, if you want to auto-print:
		doc.autoPrint();

		// Save the PDF (this will trigger a download)
		doc.save("Test Scoring Receipt " + pulledReqnum + ".pdf");
	};

	const handleTypeOfExamChange = (event) => {
		const value = event.target.value;

		if (typeOfExam === value) {
			setTypeOfExam("");
		} else if (
			state.instructorName !== "" &&
			state.deptID !== "" &&
			state.email1 !== "" &&
			state.delivery !== ""
		) {
			setTypeOfExam(value);
			setState((prevState) => ({
				...prevState,
				program: value === "multipleChoice" ? "1" : "4",
			}));
		}
	};

	const handleDoneOfficeInstructor = (openDialog) => {
		setState(RESET);
		setTypeOfExam("");
		setSearchedByCCID(false);
		setSearchState({
			ccid: "",
			firstName: "",
			lastName: "",
			email: "",
			empid: "",
		});
		setSearchError(false);
		setCCIDString("");
		setCCID("");
		setConfirmedOfficeInstructor(false);
		setDataFilled(false);
		setLoadingProfilePrefill(false);
		if (openDialog) setOpenOfficeDialog(true);
	};

	const deliveryOptions = courierDepts.includes(Number(state.deptID))
		? ["Pick Up", "Courier", "Campus Mail"]
		: ["Pick Up", "Campus Mail"];

	//Need to add checks for all text fields before next is pressed.
	return (
		<div className="flex flex-col items-center justify-start min-h-[80dvh] bg-digitalWhite">
			<OfficeDialog
				open={openOfficeDialog}
				setOpen={setOpenOfficeDialog}
				handleSearch={handleSearchByCCID}
				loadingSearchByCCID={loadingSearchByCCID}
				ccidString={ccidString}
				setCCIDString={setCCIDString}
				searchedByCCID={searchedByCCID}
				setSearchedByCCID={setSearchedByCCID}
				searchState={searchState}
				searchError={searchError}
				setSearchError={setSearchError}
				handleConfirmOfficeInstructor={handleConfirmOfficeInstructor}
			/>
			<main className="flex w-full flex-col items-center min-h-[90dvh] px-2 md:px-0">
				{/* Main Request Form */}
				<div className="relative flex w-full max-w-4xl flex-col items-center shadow my-8 rounded border bg-white p-10">
					{access === "OFFICE" && confirmedOfficeInstructor && (
						<div className="absolute top-0 left-0 px-2 py-1 rounded-md bg-primary text-white text-center w-full flex justify-between">
							<span>(OFFICE USE)</span>
							<span>
								Currently Requesting As:{" "}
								<span className="bg-white rounded-md text-primary px-1">{searchState.ccid}</span>
							</span>
							<button
								className="bg-red-700 text-white px-1 rounded-md"
								onClick={() => handleDoneOfficeInstructor(true)}
							>
								DONE/NEW INSTRUCTOR
							</button>
						</div>
					)}
					<div className="mb-4 text-center">
						<h5 className="text-xl font-medium text-primary">Exam Scanning Request Form</h5>
						<p className={`${editMode ? "text-secondaryRed" : "text-primary"} font-medium`}>
							{editMode && reqnumFromQuery
								? `(Editing Request #${reqnumFromQuery})`
								: "(New Request)"}
						</p>
					</div>
					{loadingEdit ? (
						<CircularProgress />
					) : loadingProfilePrefill ? (
						<CircularProgress />
					) : (
						<div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
							<div className="flex flex-col col-span-2">
								<label htmlFor="instructorName" className="font-medium text-primary">
									Instructor Name*
								</label>
								<input
									type="text"
									id="instructorName"
									name="instructorName"
									value={state.instructorName || ""}
									maxLength={32}
									onChange={handleChange}
									className="mt-1 p-2 border rounded w-full"
								/>
								{errorState.instructorName && (
									<span className="text-red-500 text-xs mt-1">Please enter instructor name</span>
								)}
							</div>
							{/* Primary Email */}
							<div className="flex flex-col md:col-span-1 col-span-2">
								<label htmlFor="email1" className="font-medium text-primary">
									Primary Email*{" "}
									<span className="text-xs text-gray-400">
										(Processed results will be sent here)
									</span>
								</label>
								<div className="mt-1">
									<EmailRecipientField
										placeholder="ccid@ualberta.ca"
										value={selectedPrimaryEmail}
										onChange={(recipient) => handleEmailRecipientChange("email1", recipient)}
										facultyOptions={facultyRecipientOptions}
										facultyByCcid={facultyRecipientsByCcid}
										loading={facultyRankLoading}
										error={Boolean(errorState.email1)}
										multiple={false}
										inputId="rfs-primary-email"
										inputName="email1"
										inputAutoComplete="section-rfs-primary email"
										shouldShrink={false}
									/>
								</div>
								{errorState.email1 && (
									<span className="text-red-500 text-xs mt-1">Please enter instructor email</span>
								)}
							</div>
							<div className="flex flex-col md:col-span-1 col-span-2">
								<label htmlFor="email2" className="font-medium text-primary">
									Secondary Email <span className="text-xs text-gray-400">(Optional)</span>
								</label>
								<div className="mt-1">
									<EmailRecipientField
										placeholder="optional@ualberta.ca"
										value={selectedSecondaryEmail}
										onChange={(recipient) => handleEmailRecipientChange("email2", recipient)}
										facultyOptions={facultyRecipientOptions}
										facultyByCcid={facultyRecipientsByCcid}
										loading={facultyRankLoading}
										multiple={false}
										inputId="rfs-secondary-email"
										inputName="email2"
										inputAutoComplete="section-rfs-secondary email"
										shouldShrink={false}
									/>
								</div>
							</div>
							{/* Department */}

							{/* Secondary Email */}

							{/* Pickup / Delivery */}
							<div className="flex flex-col col-span-2">
								<label htmlFor="delivery" className="font-medium text-primary relative">
									Sheet Return/Delivery*
								</label>
								<div className="flex gap-2" id="delivery">
									{deliveryOptions.map((option) => (
										<button
											key={option}
											className={`flex items-center gap-2 p-2 border rounded mb-1 disabled:bg-gray-300 disabled:cursor-not-allowed ${
												!state.delivery || state?.delivery.length === 0
													? "ring-2 animate-pulse ring-secondaryRed"
													: ""
											} ${
												state.delivery === option
													? "bg-primary text-white"
													: "bg-white border-gray-300 hover:bg-gray-50 text-gray-500"
											}`}
											onClick={() => setState({ ...state, delivery: option })}
										>
											{option}
										</button>
									))}
								</div>

								{errorState.delivery && (
									<span className="text-red-500 text-xs mt-1">{errorState.delivery}</span>
								)}
							</div>
							{state.delivery === "Campus Mail" && (
								<div className="flex flex-col col-span-2">
									<label htmlFor="address" className="font-medium text-primary relative">
										Return Address*{" "}
										<span className="text-xs text-gray-400">
											(Required for campus mail delivery)
										</span>
									</label>
									<input
										type="text"
										id="address"
										name="address"
										value={state.address || ""}
										maxLength={60}
										onChange={handleChange}
										//disabled={state.address === "Pick Up"}
										className="mt-1 p-2 border rounded w-full"
									/>
									{errorState.address && (
										<span className="text-red-500 text-xs mt-1">
											<ErrorIcon fontSize="small" /> Please enter a valid departmental return
											address for campus mail delivery.
										</span>
									)}
								</div>
							)}

							<AutoCompleteComponent
								id="outlined-basic"
								required
								label="Department"
								helperText={errorState.deptID && "Please select department"}
								error={errorState.deptID || (dataFilled && state.deptID === "")}
								name="department"
								variant="outlined"
								options={departmentsDict}
								onChange={handleChangeAutoComplete}
								value={state.deptID}
							/>

							{typeOfExam !== "multipleChoice" && typeOfExam !== "assign2" ? (
								<FormControl fullWidth className="col-span-2 " variant="outlined" required>
									<div className="font-medium text-primary">
										Select Exam Type
										{!isFormFilled && (
											<span className="text-red-400 text-xs ml-2">
												{missingRequiredFields.length > 0
													? `Missing: ${missingFieldsLabel}`
													: "(All Fields With * Required To Enable)"}
											</span>
										)}
									</div>
									<div className="flex ">
										<button
											disabled={!isFormFilled}
											onClick={() => {
												setTypeOfExam("multipleChoice");
												setState((prevState) => ({
													...prevState,
													program: "1",
												}));
											}}
											className={`  rounded-tl-md rounded-bl-md border-r-2 w-1/2 py-6 bg-primary  text-white hover:bg-darkGreen hover:text-white disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed
									
									${typeOfExam === "multipleChoice" ? "bg-primary text-white" : "bg-gray-100"}`}
										>
											<div className="flex flex-col items-center gap-2">
												Multiple Choice Exam
												<div className="flex gap-1">
													<RadioButtonUncheckedIcon />
													<RadioButtonCheckedIcon />
													<RadioButtonUncheckedIcon />
													<RadioButtonUncheckedIcon />
												</div>
											</div>
										</button>

										<button
											disabled={!isFormFilled}
											className={`  rounded-tr-md rounded-br-md border-l-2 w-1/2 bg-primary text-white  hover:bg-darkGreen hover:text-white disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed
									${typeOfExam === "assign2" ? "bg-primary text-white" : "bg-gray-100"}`}
											onClick={() => {
												setTypeOfExam("assign2");
												setState((prevState) => ({
													...prevState,
													program: "4",
												}));
											}}
										>
											<div className="flex flex-col items-center gap-2">
												Assign2 Written Exam
												<div className="flex gap-1">
													<EditIcon />
													<ArticleIcon />
													<CalculateIcon />
												</div>
												{/* <span className="text-xs">(Assign2)</span> */}
											</div>
										</button>
									</div>
								</FormControl>
							) : (
								<FormControl fullWidth className="col-span-2 " variant="outlined" required>
									<InputLabel id="exam-type-label" className="mt-4 " sx={{ color: "#275d38" }}>
										{isFormFilled
											? "Select Exam Type"
											: "Please fill out the required form fields."}
									</InputLabel>
									<Select
										labelId="exam-type-label"
										value={typeOfExam}
										disabled={!isFormFilled}
										onChange={handleTypeOfExamChange}
										label="Select Exam TypeT"
										className="mt-4"
									>
										<MenuItem value="multipleChoice">
											<div className="flex items-center gap-2">
												Multiple Choice Exam
												<div className="flex gap-1">
													<RadioButtonUncheckedIcon />
													<RadioButtonCheckedIcon />
													<RadioButtonUncheckedIcon />
													<RadioButtonUncheckedIcon />
												</div>
											</div>
										</MenuItem>

										<MenuItem value="assign2">
											<div className="flex items-center gap-2">
												Written Exam
												<div className="flex gap-1">
													<EditIcon />
													<ArticleIcon />
													<CalculateIcon />
												</div>
												{/* <span className="text-xs">(Assign2)</span> */}
											</div>
										</MenuItem>
									</Select>
								</FormControl>
							)}
						</div>
					)}
				</div>
				{typeOfExam && (
					<div
						ref={examTypeRef}
						className="flex w-full max-w-4xl flex-col items-center  border-2 mb-8  rounded-md border-t-2  bg-white p-10"
					>
						{/* Conditional Components */}
						{typeOfExam === "multipleChoice" && (
							<MultipleChoiceExam
								navigate={navigate}
								token={token}
								ccid={ccid}
								inputProps={inputProps}
								handlePrint={handlePrint}
								classNumList={classNumList}
								instructorClasses={instructorClasses}
								confirmedOfficeInstructor={confirmedOfficeInstructor}
								handleDoneOfficeInstructor={handleDoneOfficeInstructor}
								ccidString={ccidString}
								editMode={editMode}
								existingReqnum={reqnumFromQuery}
								onEditSubmit={handleEditSubmit}
							/>
						)}

						{typeOfExam === "assign2" && (
							<Assign2
								navigate={navigate}
								token={token}
								ccid={ccid}
								handlePrint={handlePrint}
								instructorClasses={instructorClasses}
								confirmedOfficeInstructor={confirmedOfficeInstructor}
								handleDoneOfficeInstructor={handleDoneOfficeInstructor}
								ccidString={ccidString}
								editMode={editMode}
								existingReqnum={reqnumFromQuery}
								onEditSubmit={handleEditSubmit}
							/>
						)}
					</div>
				)}
			</main>
			<Footer />
		</div>
	);
}
